import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, ReactElement } from 'react';
import { Controller } from 'react-hook-form';

import { IcCloseSIcon, IcArrowUp16Icon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import {
  Button,
  ButtonTypes,
  Selector,
  TextField,
  CheckboxField,
  MultiLineField,
  TimePicker,
  customScrollStyles,
  IconButton,
  IconViewSizes,
  useInputClassNames,
} from '@gem/ui-kit';

import { User } from 'APP/store/Users/User/User';

import { IEventCalendar, IUpdateEvent } from '../../Content.types';
import { Autocomplete } from './Autocomplete/Autocomplete';
import { CallLink } from './CallLink/CallLink';
import { ChipsList } from './ChipsList/ChipsList';
import { DateField } from './DateField/DateField';
import { DESCRIPTION_MAX_LENGTH, FORM_ID, TITLE_MAX_LENGTH } from './Form.constants';
import { useFormPresenter } from './Form.presenter';
import styles from './Form.styles.m.css';
import { ICalendarEventSubmitData } from './Form.types';
import { TimeZone } from './TimeZone/TimeZone';

interface IFormProps {
  event: IEventCalendar;
  updateEvent: (params: IUpdateEvent) => void;
  jumpTo: (date: Date) => void;
  handleSubmit: (params: ICalendarEventSubmitData) => Promise<void>;
  isCreate?: boolean;
  participantsInfo?: User[];
  isFormMinimized: boolean | undefined;
  onClose?: () => void;
  minimizeSideBar?: () => void;
}

export const Form: FC<IFormProps> = observer((props) => {
  const { t } = useTranslation();
  const { containerClassName, messageClassName } = useInputClassNames({ isError: true });
  const { presenter, form, removeChip, participantsValue, controlledReminderFields } =
    useFormPresenter(props);
  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting, isValid, isValidating, errors },
  } = form;
  const isAllDay = watch('allDay');

  if (props.isFormMinimized) {
    return (
      <>
        <div className={styles.topSide}>
          <div className={styles.minimizedTitleContainer}>
            <div className={styles.minimizedTitle}>{presenter.getMinimizedTitleInfo()}</div>
            <div className={styles.minimizedTime}>{presenter.getMinimizedTimeInfo()}</div>
          </div>
          <IconButton
            className={styles.close}
            Icon={IcArrowUp16Icon}
            onClick={props.minimizeSideBar}
            size={IconViewSizes.Medium}
          />
          <IconButton
            className={styles.close}
            Icon={IcCloseSIcon}
            onClick={props.onClose}
            size={IconViewSizes.Big}
          />
        </div>
        <div className={styles.bottomSide}>
          <Button
            form={FORM_ID}
            type={ButtonTypes.Submit}
            isLoading={isSubmitting}
            isDisabled={!isValid || isValidating}
          >
            {props.isCreate ? t('calendar_event_create_button') : t('common_done')}
          </Button>
        </div>
      </>
    );
  }

  return (
    <div className={styles.container}>
      <form
        id={FORM_ID}
        className={classNames(styles.form, customScrollStyles.container)}
        onSubmit={handleSubmit(presenter.onSubmit)}
      >
        <Controller
          control={control}
          name="title"
          render={({ field, fieldState }): ReactElement => (
            <TextField
              {...field}
              maxLength={TITLE_MAX_LENGTH}
              label={t('calendar_event_title')}
              hasError={fieldState.invalid}
              message={fieldState.error?.message || ' '}
              onChange={(e): void => {
                presenter.onChangeTitle(e.target.value);
                field.onChange(e.target.value);
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="participants"
          render={({ field, fieldState }): ReactElement => (
            <Autocomplete
              {...field}
              label={t('calendar_event_add_participants')}
              hasError={fieldState.invalid}
              message={fieldState.error?.message || ' '}
              removeParticipant={presenter.removeParticipant}
              setParticipant={presenter.setParticipant}
              classNameContainer={styles.fieldAutocomplete}
            />
          )}
        />
        <ChipsList
          value={participantsValue}
          selectedParticipants={presenter.selectedParticipants}
          initiatorEmail={props.event.initiatorEmail}
          removeChip={removeChip}
        />
        <Controller
          control={control}
          name="allDay"
          render={({ field, fieldState }): ReactElement => (
            <CheckboxField
              {...field}
              ref={null}
              label={t('calendar_event_all_day')}
              hasError={fieldState.invalid}
              message={fieldState.error?.message || ' '}
              className={styles.checkbox}
              onChange={presenter.onChangeCheckbox}
            />
          )}
        />
        {isAllDay ? (
          <div className={styles.containerDateRange}>
            <Controller
              control={control}
              name="start"
              render={({ field, fieldState }): ReactElement => (
                <DateField
                  {...field}
                  hasError={fieldState.invalid}
                  message={''}
                  onChange={presenter.onChangeStartAllDay}
                />
              )}
            />
            <div className={styles.divider}>&mdash;</div>
            <Controller
              control={control}
              name="end"
              render={({ field, fieldState }): ReactElement => (
                <DateField
                  {...field}
                  hasError={fieldState.invalid}
                  message={''}
                  onChange={presenter.onChangeEndAllDay}
                />
              )}
            />
          </div>
        ) : (
          <>
            <div className={styles.containerStartDate}>
              <div className={styles.title}>{t('calendar_event_starts')}</div>
              <Controller
                control={control}
                name="start"
                render={({ field, fieldState }): ReactElement => (
                  <DateField
                    {...field}
                    hasError={fieldState.invalid}
                    message={''}
                    onChange={presenter.onChangeStart}
                  />
                )}
              />
              <Controller
                control={control}
                name="startTime"
                render={({ field, fieldState }): ReactElement => (
                  <TimePicker
                    {...field}
                    hasError={fieldState.invalid}
                    message={fieldState.error?.message ? '' : ' '}
                    onChange={presenter.onChangeStartTime}
                  />
                )}
              />
            </div>
            {errors.startTime?.message && (
              <div
                className={classNames(containerClassName, messageClassName, styles.errorMessage)}
              >
                {errors.startTime?.message}
              </div>
            )}
            <div className={styles.containerEndDate}>
              <div className={styles.title}>{t('calendar_event_ends')}</div>
              <Controller
                control={control}
                name="end"
                render={({ field, fieldState }): ReactElement => (
                  <DateField
                    {...field}
                    hasError={fieldState.invalid}
                    message={''}
                    onChange={presenter.onChangeEnd}
                  />
                )}
              />
              <Controller
                control={control}
                name="endTime"
                render={({ field, fieldState }): ReactElement => (
                  <TimePicker
                    {...field}
                    hasError={fieldState.invalid}
                    message={''}
                    onChange={presenter.onChangeEndTime}
                  />
                )}
              />
            </div>
            {errors.endTime?.types && (
              <div
                className={classNames(containerClassName, messageClassName, styles.errorMessage)}
              >
                {Object.entries(errors.endTime?.types)
                  .map(([, msg]) => msg)
                  .join(' ')}
              </div>
            )}
          </>
        )}
        <Controller
          control={control}
          name="repeat"
          render={({ field }): ReactElement => (
            <Selector
              {...field}
              label={t('calendar_event_repeat')}
              items={presenter.repeatItems}
              onChange={(value: string): void => {
                presenter.onChangeRepeat(value);
                field.onChange(value);
              }}
              theme="standard"
              className={styles.repeat}
              isFullWidth
              dataTest="calendar-repeat-select-button"
            />
          )}
        />
        <CallLink link={presenter.link} />
        <TimeZone />
        <Controller
          control={control}
          name="description"
          render={({ field, fieldState }): ReactElement => (
            <MultiLineField
              {...field}
              maxLength={DESCRIPTION_MAX_LENGTH}
              rows={4}
              contentClassName={styles.fieldDescription}
              label={t('calendar_event_description')}
              hasError={fieldState.invalid}
              message={fieldState.error?.message || ' '}
              onChange={(e): void => {
                presenter.onChangeDescription(e.target.value);
                field.onChange(e.target.value);
              }}
            />
          )}
        />
        {controlledReminderFields.map((field, index) => (
          <div key={field.id}>
            <Controller
              control={control}
              name={`reminders.${index}.value`}
              render={({ field }): ReactElement => (
                <Selector
                  {...field}
                  label={t('calendar_event_reminder')}
                  onChange={(value: string): void => {
                    presenter.onChangeReminderItem(value, index);
                    field.onChange(value);
                  }}
                  items={presenter.reminderItems}
                  theme="standard"
                  className={styles.reminder}
                  isFullWidth
                  dataTest={`calendar-reminder-${index}-select-button`}
                />
              )}
            />
          </div>
        ))}
      </form>
      <div className={styles.footer}>
        <Button
          className={styles.footerButton}
          form={FORM_ID}
          type={ButtonTypes.Submit}
          isLoading={isSubmitting}
          isDisabled={!isValid || isValidating}
        >
          {props.isCreate ? t('calendar_event_create_button') : t('common_done')}
        </Button>
      </div>
    </div>
  );
});
